import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, X, Users, FileText, Clock, UtensilsCrossed, MapPin, Camera, LogIn, LogOut, Plus, MessageSquare } from 'lucide-react';
import { db, auth, googleProvider } from './firebase';
import { collection, getDocs, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';

// Real family photos from Firebase Storage
const IMAGES = {
  luigiBeatrice: 'https://firebasestorage.googleapis.com/v0/b/fontanini-family.firebasestorage.app/o/Luigi%20and%20Beatrice%20Parenti%20Fontanini.jpeg?alt=media&token=de7bd7a7-5f4e-4d06-88f5-8da346131752',
  threeBrothers: 'https://firebasestorage.googleapis.com/v0/b/fontanini-family.firebasestorage.app/o/3%20Fontanini%20brothers.jpeg?alt=media&token=83a8ae72-5f6a-4f6b-ae66-b6d0775e3537',
  family1920: 'https://firebasestorage.googleapis.com/v0/b/fontanini-family.firebasestorage.app/o/Dominic%20Fontanini%20%26%20family%20c.%201920.jpeg?alt=media&token=19849533-8fb8-40a0-a484-143dc0783333',
  marriage: 'https://firebasestorage.googleapis.com/v0/b/fontanini-family.firebasestorage.app/o/Fontanini%2C%20Domenico_Marriage%20Records.jpeg?alt=media&token=94581de1-e9ae-43de-aaa9-b0242b9a1db4',
  shipManifest1: 'https://firebasestorage.googleapis.com/v0/b/fontanini-family.firebasestorage.app/o/Fontanini%2C_Domenico_ship_man_p_1.jpg?alt=media&token=dd6a0387-0d7b-4318-b32c-ad5c124ca7c0',
  shipManifest2: 'https://firebasestorage.googleapis.com/v0/b/fontanini-family.firebasestorage.app/o/Fontanini%2C_Domenico_ship_man_p_2..jpg?alt=media&token=37b70788-6d31-4a04-a320-6442ebddd67d',
  census1925: 'https://firebasestorage.googleapis.com/v0/b/fontanini-family.firebasestorage.app/o/Fontanini%2C_Domenico_IA_1925_census_p1.tiff?alt=media&token=8000aa52-44c8-4b21-935a-5cec907e2cdb',
  recipeTowel: 'https://firebasestorage.googleapis.com/v0/b/fontanini-family.firebasestorage.app/o/IMG_0895.jpeg?alt=media&token=fb519115-9307-4f78-bf3f-055a6d44bb91',
};

// Generation colors
const genColors = {
  1: { bg: '#78350f', text: '#fef3c7', border: '#92400e' },
  2: { bg: '#92400e', text: '#fef3c7', border: '#b45309' },
  3: { bg: '#b45309', text: '#fef3c7', border: '#d97706' },
  4: { bg: '#d97706', text: '#fef3c7', border: '#f59e0b' },
  5: { bg: '#f59e0b', text: '#78350f', border: '#fbbf24' },
  6: { bg: '#fbbf24', text: '#78350f', border: '#fcd34d' },
  7: { bg: '#fcd34d', text: '#78350f', border: '#fde68a' },
};
// Olive palette for landing page
const oliveColors = {
  darkBg: '#1a1f16',        // Deep olive-black background
  midBg: '#2d3328',         // Card/frame backgrounds
  accent: '#5c6650',        // Olive green accent
  lightAccent: '#8a9a7c',   // Light olive
  cream: '#f5f2eb',         // Warm cream for text
  gold: '#c9a227',          // Pop accent for CTAs
};

// Build tree structure from flat data
function buildFamilyTree(people) {
  const peopleMap = {};
  
  // First pass: create map of all people
  people.forEach(person => {
    peopleMap[person.id] = { ...person, children: [] };
  });
  
  // Second pass: link children to parents
  people.forEach(person => {
    if (person.parents && peopleMap[person.parents]) {
      peopleMap[person.parents].children.push(peopleMap[person.id]);
    }
  });
  
  // Find root (person with no parents)
  const root = people.find(p => !p.parents);
  return root ? peopleMap[root.id] : null;
}

// Tree Node Component
function TreeNode({ person, onSelect, depth = 0, documents }) {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = person.children && person.children.length > 0;
  const colors = genColors[person.generation] || genColors[7];
  
  return (
    <div style={{ marginLeft: depth > 0 ? '32px' : '0' }}>
      <div
        onClick={() => onSelect(person)}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px',
          padding: '12px',
          borderRadius: '8px',
          cursor: 'pointer',
          backgroundColor: colors.bg,
          color: colors.text,
          border: `2px solid ${colors.border}`,
          marginBottom: '8px',
          transition: 'transform 0.2s',
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
      >
        {hasChildren ? (
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            style={{
              marginTop: '4px',
              padding: '4px',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              color: 'inherit',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        ) : (
          <div style={{ width: '32px' }} />
        )}
        
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'Georgia, serif', fontSize: '14px', fontWeight: '600' }}>
            {person.names ? person.names.join(' & ') : 'Unknown'}
          </div>
          {person.dates && (
            <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '2px' }}>{person.dates}</div>
          )}
          {person.location && (
            <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '2px' }}>{person.location}</div>
          )}
          {(person.marriageDate || person.marriagePlace) && (
            <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '2px' }}>
              Married{person.marriageDate ? ` ${person.marriageDate}` : ''}{person.marriagePlace ? ` in ${person.marriagePlace}` : ''}
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
            {person.photos && person.photos.length > 0 && (
              <span style={{
                fontSize: '11px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                padding: '2px 8px',
                borderRadius: '9999px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <Camera size={10} /> {person.photos.length}
              </span>
            )}
            {person.documents && person.documents.length > 0 && (
              <span style={{
                fontSize: '11px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                padding: '2px 8px',
                borderRadius: '9999px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <FileText size={10} /> {person.documents.length}
              </span>
            )}
          </div>
        </div>
      </div>
      
      {hasChildren && expanded && (
        <div style={{ borderLeft: '2px solid rgba(180, 83, 9, 0.3)', marginLeft: '16px' }}>
          {person.children.map(child => (
            <TreeNode key={child.id} person={child} onSelect={onSelect} depth={depth + 1} documents={documents} />
          ))}
        </div>
      )}
    </div>
  );
}

// Person Detail Modal
function PersonModal({ person, onClose, documents, isEditor, onUpdate, onPhotoUpload }) {
const [activeTab, setActiveTab] = useState('story');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    story: person.story || '',
    birthDate: person.birthDate || '',
    birthPlace: person.birthPlace || '',
    deathDate: person.deathDate || '',
    deathPlace: person.deathPlace || '',
    marriageDate: person.marriageDate || '',
    marriagePlace: person.marriagePlace || ''
  });
  // Sync editForm when person data changes
  useEffect(() => {
    setEditForm({
      story: person.story || '',
      birthDate: person.birthDate || '',
      birthPlace: person.birthPlace || '',
      deathDate: person.deathDate || '',
      deathPlace: person.deathPlace || '',
      marriageDate: person.marriageDate || '',
      marriagePlace: person.marriagePlace || ''
    });
  }, [person]);

  const colors = genColors[person.generation] || genColors[7];
  
  const personDocs = documents.filter(doc => 
    person.documents && person.documents.includes(doc.id)
  );

  const handleSave = async () => {
    await onUpdate(person.id, editForm);
    setIsEditing(false);
  };
  
  return (
    <div 
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        zIndex: 50,
        overflowY: 'auto',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: '#fef3c7',
          borderRadius: '12px',
          maxWidth: '672px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
      >
        {/* Header */}
        <div style={{
          backgroundColor: colors.bg,
          color: colors.text,
          padding: '24px',
          position: 'relative',
        }}>
          {isEditor && !isEditing && (
            <button
              onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
              style={{
              zIndex: 10,
              position: 'absolute',
                top: '16px',
                right: '60px',
                padding: '8px 12px',
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                color: 'inherit',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              Edit
            </button>
          )}
          <button
           onClick={(e) => { e.stopPropagation(); onClose(); }}
            style={{
              zIndex: 10,
              position: 'absolute',
              top: '16px',
              right: '16px',
              padding: '8px',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              cursor: 'pointer',
              color: 'inherit',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={20} />
          </button>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.7, marginBottom: '4px' }}>
            Generation {person.generation}
          </div>
          <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '28px', fontWeight: 'bold', margin: 0 }}>
            {person.names ? person.names[0] : 'Unknown'}
          </h2>
          {person.names && person.names[1] && (
            <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '20px', opacity: 0.9, margin: '4px 0 0 0', fontWeight: 'normal' }}>
              & {person.names[1]}
            </h3>
          )}
          {person.dates && <p style={{ marginTop: '8px', opacity: 0.8 }}>{person.dates}</p>}
          {person.location && <p style={{ marginTop: '4px', opacity: 0.8 }}>{person.location}</p>}
        </div>
        
        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #fcd34d' }}>
          {['story', 'photos', 'documents'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1,
                padding: '12px',
                fontSize: '14px',
                fontWeight: '500',
                textTransform: 'capitalize',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab ? '2px solid #b45309' : '2px solid transparent',
                color: activeTab === tab ? '#78350f' : '#b45309',
                cursor: 'pointer',
                transition: 'color 0.2s',
              }}
            >
              {tab}
            </button>
          ))}
        </div>
        
       {/* Content */}
        <div style={{ padding: '24px', overflowY: 'auto', maxHeight: '50vh' }}>
         {activeTab === 'story' && (
          <div>
            {isEditing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#78350f', marginBottom: '4px' }}>Story</label>
                  <textarea
                    value={editForm.story}
                    onChange={(e) => setEditForm({...editForm, story: e.target.value})}
                    style={{ width: '100%', minHeight: '120px', padding: '8px', borderRadius: '6px', border: '1px solid #d97706', fontFamily: 'inherit', fontSize: '14px' }}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: '600', color: '#78350f', marginBottom: '4px' }}>Birth Date</label>
                    <input
                      type="text"
                      value={editForm.birthDate}
                      onChange={(e) => setEditForm({...editForm, birthDate: e.target.value})}
                      placeholder="e.g., August 7, 2000"
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d97706' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: '600', color: '#78350f', marginBottom: '4px' }}>Birth Place</label>
                    <input
                      type="text"
                      value={editForm.birthPlace}
                      onChange={(e) => setEditForm({...editForm, birthPlace: e.target.value})}
                      placeholder="e.g., Des Moines, Iowa"
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d97706' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: '600', color: '#78350f', marginBottom: '4px' }}>Death Date</label>
                    <input
                      type="text"
                      value={editForm.deathDate}
                      onChange={(e) => setEditForm({...editForm, deathDate: e.target.value})}
                      placeholder="Leave blank if living"
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d97706' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: '600', color: '#78350f', marginBottom: '4px' }}>Death Place</label>
                    <input
                      type="text"
                      value={editForm.deathPlace}
                      onChange={(e) => setEditForm({...editForm, deathPlace: e.target.value})}
                      placeholder="Leave blank if living"
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d97706' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: '600', color: '#78350f', marginBottom: '4px' }}>Marriage Date</label>
                    <input
                      type="text"
                      value={editForm.marriageDate}
                      onChange={(e) => setEditForm({...editForm, marriageDate: e.target.value})}
                      placeholder="e.g., September 1909"
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d97706' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: '600', color: '#78350f', marginBottom: '4px' }}>Marriage Place</label>
                    <input
                      type="text"
                      value={editForm.marriagePlace}
                      onChange={(e) => setEditForm({...editForm, marriagePlace: e.target.value})}
                      placeholder="e.g., Frassinoro, Italy"
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d97706' }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setIsEditing(false)}
                    style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #d97706', background: 'white', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#d97706', color: 'white', cursor: 'pointer', fontWeight: '600' }}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {person.story ? (
                  <p style={{ color: '#78350f', lineHeight: 1.7 }}>{person.story}</p>
                ) : (
                  <p style={{ color: '#b45309', fontStyle: 'italic' }}>No story recorded yet.</p>
                )}
              </div>
            )}
          </div>
        )}
          
         {activeTab === 'photos' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              {isEditor && (
                <label style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '150px',
                  border: '2px dashed #d97706',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: 'rgba(217, 119, 6, 0.1)',
                }}>
                  <Plus size={32} style={{ color: '#d97706', marginBottom: '8px' }} />
                  <span style={{ color: '#d97706', fontSize: '14px', fontWeight: '500' }}>Add Photo</span>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        onPhotoUpload(person.id, file);
                      }
                    }}
                  />
                </label>
              )}
              {person.photos && person.photos.length > 0 ? (
                person.photos.map((photo, index) => {
                  // Handle both legacy keys and new URL format
                  const imageUrl = typeof photo === 'string' && photo.startsWith('http') 
                    ? photo 
                    : IMAGES[photo];
                  
                  if (!imageUrl) return null;
                  
                  return (
                    <div key={index}>
                      <img 
                        src={imageUrl} 
                        alt={`Photo ${index + 1}`}
                        style={{ width: '100%', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                      />
                    </div>
                  );
                })
              ) : (
                !isEditor && <p style={{ color: '#b45309', fontStyle: 'italic', gridColumn: '1 / -1' }}>No photos available.</p>
              )}
            </div>
          )}
          
          {activeTab === 'documents' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {personDocs.length > 0 ? (
                personDocs.map(doc => (
                  <div key={doc.id} style={{ border: '1px solid #fcd34d', borderRadius: '8px', overflow: 'hidden' }}>
                    <div style={{ backgroundColor: '#fde68a', padding: '12px' }}>
                      <h4 style={{ fontWeight: '600', color: '#78350f', margin: 0 }}>{doc.title}</h4>
                      <p style={{ fontSize: '12px', color: '#92400e', margin: '4px 0 0 0' }}>{doc.date} • {doc.location}</p>
                    </div>
                    {IMAGES[doc.imageKey] && (
                      <img 
                        src={IMAGES[doc.imageKey]} 
                        alt={doc.title}
                        style={{ width: '100%' }}
                      />
                    )}
                    {doc.description && (
                      <p style={{ padding: '12px', fontSize: '14px', color: '#92400e', backgroundColor: '#fef3c7', margin: 0 }}>
                        {doc.description}
                      </p>
                    )}
                  </div>
                ))
              ) : (
                <p style={{ color: '#b45309', fontStyle: 'italic' }}>No documents available.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Timeline Component
function Timeline({ events }) {
  const sortedEvents = [...events].sort((a, b) => {
    const yearA = parseInt(a.year) || 0;
    const yearB = parseInt(b.year) || 0;
    return yearA - yearB;
  });
  
  return (
    <div style={{ position: 'relative' }}>
      <div style={{
        position: 'absolute',
        left: '16px',
        top: 0,
        bottom: 0,
        width: '2px',
        backgroundColor: '#fcd34d',
      }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {sortedEvents.map((event, i) => (
          <div key={event.id || i} style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', paddingLeft: '8px' }}>
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: '#d97706',
              border: '4px solid #fde68a',
              zIndex: 10,
              flexShrink: 0,
              marginTop: '4px',
            }} />
            <div>
              <span style={{ fontFamily: 'monospace', color: '#92400e', fontSize: '14px', fontWeight: 'bold' }}>
                {event.year}
              </span>
              <p style={{ color: '#78350f', margin: '4px 0 0 0' }}>{event.event}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Recipe Component
function Recipe({ recipe }) {
  if (!recipe) {
    return <p style={{ color: '#b45309', fontStyle: 'italic' }}>No recipe available.</p>;
  }
  
  return (
    <div style={{
      backgroundColor: '#fde68a',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    }}>
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: '#78350f', fontWeight: 'bold', margin: 0 }}>
          {recipe.title}
        </h3>
        <p style={{ color: '#92400e', margin: '4px 0 0 0' }}>{recipe.yield}</p>
      </div>
      
      <p style={{ color: '#92400e', marginBottom: '24px', fontStyle: 'italic' }}>{recipe.story}</p>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
        <div>
          <h4 style={{ fontWeight: '600', color: '#78350f', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              width: '24px',
              height: '24px',
              backgroundColor: '#d97706',
              color: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
            }}>1</span>
            Ingredients
          </h4>
          <ul style={{ fontSize: '14px', color: '#92400e', listStyle: 'none', padding: 0, margin: 0 }}>
            {recipe.ingredients && recipe.ingredients.map((ing, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '4px' }}>
                <span style={{ color: '#d97706' }}>•</span>
                {ing}
              </li>
            ))}
          </ul>
        </div>
        
        <div>
          <h4 style={{ fontWeight: '600', color: '#78350f', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              width: '24px',
              height: '24px',
              backgroundColor: '#d97706',
              color: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
            }}>2</span>
            Instructions
          </h4>
          <ol style={{ fontSize: '14px', color: '#92400e', listStyle: 'none', padding: 0, margin: 0 }}>
            {recipe.instructions && recipe.instructions.map((step, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                <span style={{ color: '#d97706', fontWeight: 'bold' }}>{i + 1}.</span>
                {step}
              </li>
            ))}
          </ol>
          
          {recipe.notes && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: 'rgba(217, 119, 6, 0.2)',
              borderRadius: '8px',
            }}>
              <p style={{ fontSize: '12px', color: '#92400e', fontStyle: 'italic', margin: 0 }}>
                <strong>Notes:</strong> {recipe.notes}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Documents Gallery Component
function DocumentsGallery({ documents }) {
  const [selectedDoc, setSelectedDoc] = useState(null);
  
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
        {documents.map(doc => (
          <div
            key={doc.id}
            onClick={() => setSelectedDoc(doc)}
            style={{ cursor: 'pointer' }}
          >
            <div style={{
              aspectRatio: '4/3',
              overflow: 'hidden',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}>
              {IMAGES[doc.imageKey] && (
                <img 
                  src={IMAGES[doc.imageKey]} 
                  alt={doc.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                />
              )}
            </div>
            <p style={{ marginTop: '8px', fontSize: '14px', color: '#92400e', fontWeight: '500' }}>{doc.title}</p>
            <p style={{ fontSize: '12px', color: '#b45309' }}>{doc.date}</p>
          </div>
        ))}
      </div>
      
      {selectedDoc && (
        <div 
          onClick={() => setSelectedDoc(null)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            zIndex: 50,
          }}
        >
          <div 
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: '896px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              backgroundColor: 'white',
              borderRadius: '12px',
            }}
          >
            <div style={{
              position: 'sticky',
              top: 0,
              backgroundColor: '#78350f',
              color: '#fef3c7',
              padding: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}>
              <div>
                <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '20px', fontWeight: 'bold', margin: 0 }}>{selectedDoc.title}</h3>
                <p style={{ fontSize: '14px', opacity: 0.8, margin: '4px 0 0 0' }}>{selectedDoc.date} • {selectedDoc.location}</p>
              </div>
              <button 
                onClick={() => setSelectedDoc(null)}
                style={{
                  padding: '8px',
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  color: 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <X size={20} />
              </button>
            </div>
            {IMAGES[selectedDoc.imageKey] && (
              <img src={IMAGES[selectedDoc.imageKey]} alt={selectedDoc.title} style={{ width: '100%' }} />
            )}
            <p style={{ padding: '16px', color: '#92400e', margin: 0 }}>{selectedDoc.description}</p>
          </div>
        </div>
      )}
    </div>
  );
}
// Landing Page Component
function LandingPage({ onEnter, familyTree }) {
  const [photos, setPhotos] = useState([]);
  
  // Collect all photos from the family tree
  useEffect(() => {
    if (!familyTree) return;
    
    const allPhotos = [];
    
    function collectPhotos(node) {
      if (!node) return;
      
      // Add photos from this person
      if (node.photos && node.photos.length > 0) {
        node.photos.forEach(photo => {
          const imageUrl = typeof photo === 'string' && photo.startsWith('http') 
            ? photo 
            : IMAGES[photo];
          if (imageUrl) {
            allPhotos.push({
              url: imageUrl,
              personId: node.id,
              personName: node.names ? node.names.join(' & ') : 'Unknown',
            });
          }
        });
      }
      
      // Recurse through children
      if (node.children) {
        node.children.forEach(child => collectPhotos(child));
      }
    }
    
    collectPhotos(familyTree);
    
   // Add standalone legacy images only if not already present
    const legacyImages = [
      { url: IMAGES.luigiBeatrice, personName: 'Luigi & Beatrice' },
      { url: IMAGES.threeBrothers, personName: 'The Three Brothers' },
      { url: IMAGES.family1920, personName: 'Family c. 1920' },
      { url: IMAGES.recipeTowel, personName: "Grandma's Recipe" },
    ];
    
    legacyImages.forEach(legacy => {
      if (legacy.url && !allPhotos.find(p => p.url === legacy.url)) {
        allPhotos.push(legacy);
      }
    });
    
    // Remove any duplicates by URL, shuffle, and take exactly 5
    const uniqueUrls = new Set();
    const uniquePhotos = allPhotos.filter(photo => {
      if (uniqueUrls.has(photo.url)) return false;
      uniqueUrls.add(photo.url);
      return true;
    });
    
    const shuffled = uniquePhotos.sort(() => Math.random() - 0.5);
    setPhotos(shuffled.slice(0, 5));
  }, [familyTree]);
  
  // Slight rotation for each polaroid
  const rotations = [-8, -3, 0, 3, 8];
  
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: oliveColors.darkBg,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
    }}>
      {/* Row of Polaroids */}
      <div style={{
        display: 'flex',
        gap: 'clamp(8px, 2vw, 24px)',
        marginBottom: 'clamp(24px, 4vh, 48px)',
        flexWrap: 'nowrap',
        justifyContent: 'center',
        padding: '10px',
        maxWidth: '100%',
      }}>
        {photos.map((photo, index) => (
          <div
            key={index}
            style={{
              transform: `rotate(${rotations[index] || 0}deg)`,
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              cursor: 'pointer',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = `rotate(${rotations[index] || 0}deg) scale(1.05) translateY(-8px)`;
              e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = `rotate(${rotations[index] || 0}deg)`;
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.3)';
            }}
          >
            {/* Polaroid frame */}
            <div style={{
              backgroundColor: oliveColors.cream,
              padding: 'clamp(4px, 1vw, 10px)',
              paddingBottom: 'clamp(20px, 4vw, 36px)',
              boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
              width: 'clamp(60px, 12vw, 140px)',
              position: 'relative',
            }}>
              <img
                src={photo.url}
                alt={photo.personName}
                style={{
                  width: '100%',
                  height: 'clamp(60px, 12vw, 140px)',
                  objectFit: 'cover',
                  display: 'block',
                  filter: 'sepia(15%) contrast(95%)',
                }}
              />
              <p style={{
                position: 'absolute',
                bottom: 'clamp(3px, 0.8vw, 8px)',
                left: 'clamp(4px, 1vw, 10px)',
                right: 'clamp(4px, 1vw, 10px)',
                margin: 0,
                fontFamily: "'Courier New', monospace",
                fontSize: 'clamp(6px, 1.2vw, 10px)',
                color: oliveColors.accent,
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {photo.personName}
              </p>
            </div>
          </div>
        ))}
      </div>
      
      {/* Center content */}
      <div style={{ textAlign: 'center' }}>
        <h1 style={{
          fontFamily: 'Georgia, serif',
          fontSize: '52px',
          fontWeight: 'normal',
          color: oliveColors.cream,
          margin: '0 0 12px 0',
          letterSpacing: '0.02em',
        }}>
          The Fontanini Family
        </h1>
        <p style={{
          fontFamily: 'Georgia, serif',
          fontSize: '20px',
          color: oliveColors.lightAccent,
          margin: '0 0 24px 0',
          fontStyle: 'italic',
        }}>
          Frassinoro → Des Moines → America
        </p>
        <p style={{
          fontSize: '13px',
          color: oliveColors.accent,
          margin: '0 0 48px 0',
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
        }}>
          Seven Generations • 1909–2026
        </p>
        <button
          onClick={onEnter}
          style={{
            padding: '16px 56px',
            fontSize: '14px',
            fontFamily: 'Georgia, serif',
            backgroundColor: 'transparent',
            border: `2px solid ${oliveColors.gold}`,
            color: oliveColors.gold,
            cursor: 'pointer',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = oliveColors.gold;
            e.currentTarget.style.color = oliveColors.darkBg;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = oliveColors.gold;
          }}
        >
          Enter
        </button>
      </div>
      
      {/* Bottom attribution */}
      <div style={{
        position: 'absolute',
        bottom: '24px',
        left: 0,
        right: 0,
        textAlign: 'center',
      }}>
        <p style={{
          fontSize: '12px',
          color: oliveColors.accent,
          margin: 0,
          letterSpacing: '0.05em',
        }}>
          From the mountains of Emilia-Romagna to the heartland of America
        </p>
      </div>
    </div>
  );
}
// Main App Component
export default function App() {
  const [showLanding, setShowLanding] = useState(true);
  const [activeView, setActiveView] = useState('tree');
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [familyTree, setFamilyTree] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [isEditor, setIsEditor] = useState(false);
  const [feedback, setFeedback] = useState([]);
  const [showAddFeedback, setShowAddFeedback] = useState(false);
  const [newFeedback, setNewFeedback] = useState({
    title: '',
    description: '',
    type: 'Feature Request'
  });
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [showAddRecipe, setShowAddRecipe] = useState(false);
  const [newRecipe, setNewRecipe] = useState({
    title: '',
    yield: '',
    story: '',
    ingredients: '',
    instructions: '',
    notes: ''
  });
  const [editingPerson, setEditingPerson] = useState(null);
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [newPerson, setNewPerson] = useState({
    name1: '',
    name2: '',
    generation: '',
    dates: '',
    location: '',
    birthDate: '',
    birthPlace: '',
    deathDate: '',
    deathPlace: '',
    marriageDate: '',
    marriagePlace: '',
    story: '',
    parents: ''
  });

  // Listen for auth state changes and check editor status
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        // Check if user is an approved editor
        const settingsDoc = await getDoc(doc(db, 'settings', 'approvedEditors'));
        if (settingsDoc.exists()) {
          const approvedEmails = settingsDoc.data().emails || [];
          setIsEditor(approvedEmails.includes(currentUser.email));
        }
      } else {
        setIsEditor(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Auth functions
  const handleLogin = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };
  
  // Add recipe function
  const handleAddRecipe = async () => {
    if (!newRecipe.title) return;
    
    try {
      const recipeData = {
        title: newRecipe.title,
        yield: newRecipe.yield,
        story: newRecipe.story,
        ingredients: newRecipe.ingredients.split('\n').filter(i => i.trim()),
        instructions: newRecipe.instructions.split('\n').filter(i => i.trim()),
        notes: newRecipe.notes,
        addedBy: user?.displayName || user?.email || 'Unknown',
        addedAt: new Date().toISOString()
      };
      
      await addDoc(collection(db, 'recipes'), recipeData);
      
      // Refresh recipes
      const recipesSnapshot = await getDocs(collection(db, 'recipes'));
      const recipesData = recipesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRecipes(recipesData);
      
      // Reset form
      setNewRecipe({ title: '', yield: '', story: '', ingredients: '', instructions: '', notes: '' });
      setShowAddRecipe(false);
    } catch (error) {
      console.error('Error adding recipe:', error);
    }
  };
  // Add feedback function
  const handleAddFeedback = async () => {
    if (!newFeedback.title) return;
    
    try {
      const feedbackData = {
        title: newFeedback.title,
        description: newFeedback.description,
        type: newFeedback.type,
        submittedBy: user?.displayName || user?.email || 'Unknown',
        submittedByEmail: user?.email,
        submittedAt: new Date().toISOString(),
        status: 'New',
        priority: '',
        adminNotes: ''
      };
      
      await addDoc(collection(db, 'feedback'), feedbackData);
      
      // Refresh feedback
      const feedbackSnapshot = await getDocs(collection(db, 'feedback'));
      const feedbackList = feedbackSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFeedback(feedbackList);
      
      // Reset form
      setNewFeedback({ title: '', description: '', type: 'Feature Request' });
      setShowAddFeedback(false);
    } catch (error) {
      console.error('Error adding feedback:', error);
    }
  };
  // Update feedback function (admin only)
  const handleUpdateFeedback = async (feedbackId, updates) => {
    if (!isEditor) return;
    
    try {
      await updateDoc(doc(db, 'feedback', feedbackId), updates);
      
      // Refresh feedback
      const feedbackSnapshot = await getDocs(collection(db, 'feedback'));
      const feedbackList = feedbackSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFeedback(feedbackList);
      setEditingFeedback(null);
    } catch (error) {
      console.error('Error updating feedback:', error);
    }
  };
  // Update person function (admin only)
  const handleUpdatePerson = async (personId, updates) => {
    if (!isEditor) return;
    
    try {
      await updateDoc(doc(db, 'people', personId), updates);
      
      // Refresh people data
      const peopleSnapshot = await getDocs(collection(db, 'people'));
      const people = peopleSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const tree = buildFamilyTree(people);
        setFamilyTree(tree);
        // Update selectedPerson with new data
        const updatedPerson = people.find(p => p.id === personId);
        if (updatedPerson) {
          setSelectedPerson(updatedPerson);
        }
    } catch (error) {
      console.error('Error updating person:', error);
    }
  };  
  // Upload photo function (editor only)
  const handlePhotoUpload = async (personId, file) => {
    if (!isEditor || !file) return;
    
    try {
      // Import storage functions
      const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
      const storage = getStorage();
      
      // Create unique filename
      const timestamp = Date.now();
      const filename = `photos/${personId}/${timestamp}_${file.name}`;
      const storageRef = ref(storage, filename);
      
      // Upload file
      await uploadBytes(storageRef, file);
      
      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);
      
      // Get current person data
      const personDoc = await getDoc(doc(db, 'people', personId));
      const currentPhotos = personDoc.data()?.photos || [];
      
      // Add new URL to photos array
      await updateDoc(doc(db, 'people', personId), {
        photos: [...currentPhotos, downloadURL]
      });
      
      // Refresh people data
      const peopleSnapshot = await getDocs(collection(db, 'people'));
      const people = peopleSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const tree = buildFamilyTree(people);
      setFamilyTree(tree);
      
      // Update selectedPerson with new data
      const updatedPerson = people.find(p => p.id === personId);
      if (updatedPerson) {
        setSelectedPerson(updatedPerson);
      }
      
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Error uploading photo. Please try again.');
    }
  };

  // Add person function (editor only)
  const handleAddPerson = async () => {
    if (!isEditor || !newPerson.name1) return;
    
    try {
      const personData = {
        names: newPerson.name2 
          ? [newPerson.name1, newPerson.name2] 
          : [newPerson.name1],
        generation: parseInt(newPerson.generation) || 1,
        dates: newPerson.dates,
        location: newPerson.location,
        birthDate: newPerson.birthDate,
        birthPlace: newPerson.birthPlace,
        deathDate: newPerson.deathDate,
        deathPlace: newPerson.deathPlace,
        marriageDate: newPerson.marriageDate,
        marriagePlace: newPerson.marriagePlace,
        story: newPerson.story,
        parents: newPerson.parents || null,
        photos: [],
        documents: []
      };
      
      await addDoc(collection(db, 'people'), personData);
      
      // Refresh people data
      const peopleSnapshot = await getDocs(collection(db, 'people'));
      const people = peopleSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const tree = buildFamilyTree(people);
      setFamilyTree(tree);
      
      // Reset form and close modal
      setNewPerson({
        name1: '',
        name2: '',
        generation: '',
        dates: '',
        location: '',
        birthDate: '',
        birthPlace: '',
        deathDate: '',
        deathPlace: '',
        marriageDate: '',
        marriagePlace: '',
        story: '',
        parents: ''
      });
      setShowAddPerson(false);
      
    } catch (error) {
      console.error('Error adding person:', error);
      alert('Error adding person. Please try again.');
    }
  };
  
  // Fetch data from Firestore
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        
        // Fetch people
        const peopleSnapshot = await getDocs(collection(db, 'people'));
        const people = peopleSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Build tree
        const tree = buildFamilyTree(people);
        setFamilyTree(tree);
        
        // Fetch documents
        const docsSnapshot = await getDocs(collection(db, 'documents'));
        const docs = docsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setDocuments(docs);
        
        // Fetch recipes
        const recipesSnapshot = await getDocs(collection(db, 'recipes'));
        const recipesData = recipesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRecipes(recipesData);
        
        // Fetch timeline
        const timelineSnapshot = await getDocs(collection(db, 'timeline'));
        const timelineData = timelineSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTimeline(timelineData);

        // Fetch feedback
          const feedbackSnapshot = await getDocs(collection(db, 'feedback'));
          const feedbackData = feedbackSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setFeedback(feedbackData);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
        setLoading(false);
      }
    }
    
    fetchData();
  }, []);
  
  const navItems = [
    { id: 'tree', label: 'Family Tree', icon: Users },
    { id: 'timeline', label: 'Timeline', icon: Clock },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'recipe', label: 'Recipe', icon: UtensilsCrossed },
    { id: 'feedback', label: 'Feedback', icon: MessageSquare },
  ];
  
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fef3c7',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #fcd34d',
            borderTopColor: '#d97706',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px',
          }} />
          <p style={{ color: '#92400e', fontFamily: 'Georgia, serif' }}>Loading family history...</p>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fef3c7',
        padding: '16px',
      }}>
        <div style={{ textAlign: 'center', maxWidth: '400px' }}>
          <p style={{ color: '#dc2626', marginBottom: '16px' }}>Error loading family data:</p>
          <p style={{ color: '#92400e', fontSize: '14px' }}>{error}</p>
        </div>
      </div>
    );
  }
  
  if (showLanding) {
    return <LandingPage onEnter={() => setShowLanding(false)} familyTree={familyTree} />;
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom, #fde68a, #fef3c7)' }}>
      {/* Header */}
      <header style={{ backgroundColor: '#78350f', color: '#fef3c7' }}>
        <div style={{ maxWidth: '1152px', margin: '0 auto', padding: '32px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <MapPin size={20} style={{ color: '#fbbf24' }} />
              <span style={{ color: '#fbbf24', fontSize: '14px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Frassinoro → Des Moines → America
              </span>
            </div>
            <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '36px', fontWeight: 'bold', margin: 0 }}>
              The Fontanini Family
            </h1>
            <p style={{ marginTop: '8px', color: '#fcd34d', fontSize: '18px' }}>
              From the mountains of Emilia-Romagna to the heartland of America
            </p>
            <p style={{ marginTop: '4px', color: 'rgba(251, 191, 36, 0.7)', fontSize: '14px' }}>
              1909 – 2026 • Seven Generations
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {user ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ color: '#fcd34d', fontSize: '14px' }}>{user.displayName || user.email}</span>
                <button
                  onClick={handleLogout}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    backgroundColor: 'rgba(251, 191, 36, 0.2)',
                    border: '1px solid #fbbf24',
                    borderRadius: '6px',
                    color: '#fcd34d',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  <LogOut size={16} /> Sign Out
                </button>
              </div>
            ) : (
              <button
                onClick={handleLogin}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  backgroundColor: '#fbbf24',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#78350f',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                <LogIn size={16} /> Sign In
              </button>
            )}
          </div>
        </div>
      </header>
      
      {/* Navigation */}
      <nav style={{
        position: 'sticky',
        top: 0,
        backgroundColor: 'rgba(120, 53, 15, 0.95)',
        backdropFilter: 'blur(4px)',
        zIndex: 40,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      }}>
        <div style={{ maxWidth: '1152px', margin: '0 auto', padding: '0 16px' }}>
          <div style={{ display: 'flex', overflowX: 'auto' }}>
            {navItems.map(item => (
              <button
                key={item.id}
                onClick={() => setActiveView(item.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  whiteSpace: 'nowrap',
                  background: 'none',
                  border: 'none',
                  borderBottom: activeView === item.id ? '2px solid #fbbf24' : '2px solid transparent',
                  color: activeView === item.id ? '#fef3c7' : '#fbbf24',
                  cursor: 'pointer',
                  transition: 'color 0.2s',
                }}
              >
                <item.icon size={16} />
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </nav>
      
      {/* Main Content */}
      <main style={{ maxWidth: '1152px', margin: '0 auto', padding: '32px 16px' }}>
     {activeView === 'tree' && (
          <div>
            <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: '#78350f', fontWeight: 'bold', marginBottom: '8px' }}>
                  Interactive Family Tree
                </h2>
                <p style={{ color: '#b45309', margin: 0 }}>
                  Click any name to see their story, photos, and documents. Click the arrows to expand or collapse branches.
                </p>
              </div>
              {isEditor && (
                <button
                  onClick={() => setShowAddPerson(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '10px 16px',
                    backgroundColor: '#78350f',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#fef3c7',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  <Plus size={16} /> Add Person
                </button>
              )}
            </div>
            {familyTree ? (
              <TreeNode person={familyTree} onSelect={setSelectedPerson} documents={documents} />
            ) : (
              <p style={{ color: '#b45309', fontStyle: 'italic' }}>No family data available.</p>
            )}
          </div>
        )}
        
        {activeView === 'timeline' && (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: '#78350f', fontWeight: 'bold', marginBottom: '8px' }}>
                Family Timeline
              </h2>
              <p style={{ color: '#b45309', margin: 0 }}>
                Key moments in the Fontanini family journey across 117 years.
              </p>
            </div>
            <Timeline events={timeline} />
          </div>
        )}
        
        {activeView === 'documents' && (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: '#78350f', fontWeight: 'bold', marginBottom: '8px' }}>
                Historical Documents
              </h2>
              <p style={{ color: '#b45309', margin: 0 }}>
                Ship manifests, census records, and Italian civil documents. Click any document to view full size.
              </p>
            </div>
            <DocumentsGallery documents={documents} />
          </div>
        )}
        
        {activeView === 'recipe' && (
              <div>
                <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: '#78350f', fontWeight: 'bold', marginBottom: '8px' }}>
                      Family Recipes
                    </h2>
                    <p style={{ color: '#b45309', margin: 0 }}>
                      Preserved traditions from the Italian-American community of Des Moines.
                    </p>
                  </div>
                  {isEditor && (
                    <button
                      onClick={() => setShowAddRecipe(true)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '10px 16px',
                        backgroundColor: '#78350f',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#fef3c7',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                    >
                      <Plus size={16} /> Add Recipe
                    </button>
                  )}
                </div>
                {recipes.map((recipe, index) => (
                  <Recipe key={recipe.id || index} recipe={recipe} />
                ))}
                {recipes.length === 0 && (
                  <p style={{ color: '#b45309', fontStyle: 'italic' }}>No recipes yet. Be the first to add one!</p>
                )}
              </div>
            )}
            {activeView === 'feedback' && (
              <div>
                <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: '#78350f', fontWeight: 'bold', marginBottom: '8px' }}>
                      Feedback & Suggestions
                    </h2>
                    <p style={{ color: '#b45309', margin: 0 }}>
                      Help us improve the Fontanini Family app.
                    </p>
                  </div>
                  {user && (
                    <button
                      onClick={() => setShowAddFeedback(true)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '10px 16px',
                        backgroundColor: '#78350f',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#fef3c7',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                    >
                      <Plus size={16} /> Submit Feedback
                    </button>
                  )}
                </div>
                
                {!user && (
                  <p style={{ color: '#b45309', fontStyle: 'italic' }}>Sign in to submit feedback or view submissions.</p>
                )}
                
                {user && feedback.length === 0 && (
                  <p style={{ color: '#b45309', fontStyle: 'italic' }}>No feedback yet. Be the first to submit!</p>
                )}
                
                {user && feedback.map((item) => (
                  <div key={item.id} style={{
                    backgroundColor: '#fef3c7',
                    border: '1px solid #d97706',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '12px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <h3 style={{ color: '#78350f', margin: 0, fontSize: '18px' }}>{item.title}</h3>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          backgroundColor: item.type === 'Bug' ? '#fecaca' : item.type === 'Feature Request' ? '#d1fae5' : '#e0e7ff',
                          color: item.type === 'Bug' ? '#991b1b' : item.type === 'Feature Request' ? '#065f46' : '#3730a3'
                        }}>{item.type}</span>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          backgroundColor: item.status === 'New' ? '#fef3c7' : item.status === 'In Progress' ? '#dbeafe' : item.status === 'Completed' ? '#d1fae5' : '#f3f4f6',
                          color: item.status === 'New' ? '#92400e' : item.status === 'In Progress' ? '#1e40af' : item.status === 'Completed' ? '#065f46' : '#374151'
                        }}>{item.status}</span>
                      </div>
                    </div>
                    <p style={{ color: '#92400e', margin: '0 0 8px 0', fontSize: '14px' }}>{item.description}</p>
                    <p style={{ color: '#b45309', margin: 0, fontSize: '12px' }}>
                      Submitted by {item.submittedBy} on {new Date(item.submittedAt).toLocaleDateString()}
                    </p>
                    {isEditor && item.adminNotes && (
                      <p style={{ color: '#78350f', margin: '8px 0 0 0', fontSize: '12px', fontStyle: 'italic' }}>
                        Admin notes: {item.adminNotes}
                      </p>
                    )}
                    {isEditor && (
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #d97706' }}>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <select
                            value={item.status}
                            onChange={(e) => handleUpdateFeedback(item.id, { status: e.target.value })}
                            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #d97706', fontSize: '12px' }}
                          >
                            <option value="New">New</option>
                            <option value="Reviewed">Reviewed</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Completed">Completed</option>
                            <option value="Won't Do">Won't Do</option>
                          </select>
                          <select
                            value={item.priority || ''}
                            onChange={(e) => handleUpdateFeedback(item.id, { priority: e.target.value })}
                            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #d97706', fontSize: '12px' }}
                          >
                            <option value="">No Priority</option>
                            <option value="High">High</option>
                            <option value="Medium">Medium</option>
                            <option value="Low">Low</option>
                          </select>
                          <input
                            type="text"
                            placeholder="Admin notes..."
                            defaultValue={item.adminNotes || ''}
                            onBlur={(e) => handleUpdateFeedback(item.id, { adminNotes: e.target.value })}
                            style={{ flex: 1, minWidth: '200px', padding: '6px 10px', borderRadius: '4px', border: '1px solid #d97706', fontSize: '12px' }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
      </main>
      
      {/* Footer */}
      <footer style={{ backgroundColor: '#78350f', color: '#fcd34d', padding: '24px 16px', marginTop: '48px' }}>
        <div style={{ maxWidth: '1152px', margin: '0 auto', textAlign: 'center', fontSize: '14px' }}>
          <p style={{ margin: 0 }}>Fontanini Family History • Compiled from immigration records, census documents, and family photographs</p>
          <p style={{ marginTop: '8px', color: '#fbbf24' }}>Documents shared by Bobby Ellis • Recipe towel gift from Michele Gough, Christmas 2025</p>
          <p style={{ marginTop: '8px', opacity: 0.7, fontSize: '12px' }}>Data powered by Firebase • Built with React</p>
        </div>
      </footer>
      
      {/* Person Modal */}
      {selectedPerson && (
       <PersonModal person={selectedPerson} onClose={() => setSelectedPerson(null)} documents={documents} isEditor={isEditor} onUpdate={handleUpdatePerson} onPhotoUpload={handlePhotoUpload} />
      )}
      {/* Add Recipe Modal */}
      {showAddRecipe && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          zIndex: 50
        }} onClick={() => setShowAddRecipe(false)}>
          <div style={{
            backgroundColor: '#fef3c7',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ backgroundColor: '#78350f', color: '#fef3c7', padding: '24px', borderRadius: '12px 12px 0 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', margin: 0 }}>Add New Recipe</h3>
                <button onClick={() => setShowAddRecipe(false)} style={{ background: 'none', border: 'none', color: '#fef3c7', cursor: 'pointer' }}>
                  <X size={24} />
                </button>
              </div>
            </div>
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Recipe Title *</label>
                <input
                  type="text"
                  value={newRecipe.title}
                  onChange={e => setNewRecipe({...newRecipe, title: e.target.value})}
                  placeholder="e.g., Grandma's Flour Tortillas"
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '16px', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Yield</label>
                <input
                  type="text"
                  value={newRecipe.yield}
                  onChange={e => setNewRecipe({...newRecipe, yield: e.target.value})}
                  placeholder="e.g., Makes 12 tortillas"
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '16px', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Story (optional)</label>
                <textarea
                  value={newRecipe.story}
                  onChange={e => setNewRecipe({...newRecipe, story: e.target.value})}
                  placeholder="The story behind this recipe..."
                  rows={3}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '16px', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Ingredients (one per line)</label>
                <textarea
                  value={newRecipe.ingredients}
                  onChange={e => setNewRecipe({...newRecipe, ingredients: e.target.value})}
                  placeholder="2 cups flour&#10;1 tsp salt&#10;..."
                  rows={6}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '16px', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Instructions (one step per line)</label>
                <textarea
                  value={newRecipe.instructions}
                  onChange={e => setNewRecipe({...newRecipe, instructions: e.target.value})}
                  placeholder="Mix dry ingredients&#10;Add water slowly&#10;..."
                  rows={6}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '16px', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Notes (optional)</label>
                <textarea
                  value={newRecipe.notes}
                  onChange={e => setNewRecipe({...newRecipe, notes: e.target.value})}
                  placeholder="Any tips or variations..."
                  rows={2}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '16px', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowAddRecipe(false)}
                  style={{ padding: '10px 20px', borderRadius: '6px', border: '1px solid #d97706', backgroundColor: 'transparent', color: '#78350f', cursor: 'pointer', fontSize: '14px' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddRecipe}
                  style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#78350f', color: '#fef3c7', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
                >
                  Save Recipe
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Add Feedback Modal */}
      {showAddFeedback && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          zIndex: 50
        }} onClick={() => setShowAddFeedback(false)}>
          <div style={{
            backgroundColor: '#fef3c7',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ backgroundColor: '#78350f', color: '#fef3c7', padding: '24px', borderRadius: '12px 12px 0 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', margin: 0 }}>Submit Feedback</h3>
                <button onClick={() => setShowAddFeedback(false)} style={{ background: 'none', border: 'none', color: '#fef3c7', cursor: 'pointer' }}>
                  <X size={24} />
                </button>
              </div>
            </div>
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Title *</label>
                <input
                  type="text"
                  value={newFeedback.title}
                  onChange={e => setNewFeedback({...newFeedback, title: e.target.value})}
                  placeholder="Brief summary of your feedback"
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '16px', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Type</label>
                <select
                  value={newFeedback.type}
                  onChange={e => setNewFeedback({...newFeedback, type: e.target.value})}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '16px', boxSizing: 'border-box' }}
                >
                  <option value="Feature Request">Feature Request</option>
                  <option value="Bug">Bug</option>
                  <option value="Question">Question</option>
                </select>
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Description</label>
                <textarea
                  value={newFeedback.description}
                  onChange={e => setNewFeedback({...newFeedback, description: e.target.value})}
                  placeholder="Describe your feedback in detail..."
                  rows={4}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '16px', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowAddFeedback(false)}
                  style={{ padding: '10px 20px', borderRadius: '6px', border: '1px solid #d97706', backgroundColor: 'transparent', color: '#78350f', cursor: 'pointer', fontSize: '14px' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddFeedback}
                  style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#78350f', color: '#fef3c7', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Add Person Modal */}
      {showAddPerson && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          zIndex: 50
        }} onClick={() => setShowAddPerson(false)}>
          <div style={{
            backgroundColor: '#fef3c7',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ backgroundColor: '#78350f', color: '#fef3c7', padding: '24px', borderRadius: '12px 12px 0 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', margin: 0 }}>Add New Person</h3>
                <button onClick={() => setShowAddPerson(false)} style={{ background: 'none', border: 'none', color: '#fef3c7', cursor: 'pointer' }}>
                  <X size={24} />
                </button>
              </div>
            </div>
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>First Person Name *</label>
                  <input
                    type="text"
                    value={newPerson.name1}
                    onChange={e => setNewPerson({...newPerson, name1: e.target.value})}
                    placeholder="e.g., John James Fontanini"
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Second Person Name (Spouse)</label>
                  <input
                    type="text"
                    value={newPerson.name2}
                    onChange={e => setNewPerson({...newPerson, name2: e.target.value})}
                    placeholder="e.g., Velma Fontanini"
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Generation *</label>
                  <select
                    value={newPerson.generation}
                    onChange={e => setNewPerson({...newPerson, generation: e.target.value})}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '14px', boxSizing: 'border-box' }}
                  >
                    <option value="">Select generation...</option>
                    <option value="1">1 - Luigi & Beatrice (Italy)</option>
                    <option value="2">2 - Domenico & Maria (Immigrants)</option>
                    <option value="3">3 - John Sr. & Velma</option>
                    <option value="4">4 - John Jr., Judy, Don, etc.</option>
                    <option value="5">5 - Jay, Michele, Bruce Jr.</option>
                    <option value="6">6 - Adriana, John IV, Victoria, etc.</option>
                    <option value="7">7 - Samuel (expected)</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Parent (select)</label>
                  <select
                    value={newPerson.parents}
                    onChange={e => setNewPerson({...newPerson, parents: e.target.value})}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '14px', boxSizing: 'border-box' }}
                  >
                    <option value="">No parent (root)</option>
                    {familyTree && (function flattenTree(node, list = []) {
                      if (node) {
                        list.push(node);
                        if (node.children) {
                          node.children.forEach(child => flattenTree(child, list));
                        }
                      }
                      return list;
                    })(familyTree).map(p => (
                      <option key={p.id} value={p.id}>
                        {p.names ? p.names.join(' & ') : 'Unknown'}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Dates (display)</label>
                  <input
                    type="text"
                    value={newPerson.dates}
                    onChange={e => setNewPerson({...newPerson, dates: e.target.value})}
                    placeholder="e.g., 1887-1965 or b. 2000"
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Location</label>
                  <input
                    type="text"
                    value={newPerson.location}
                    onChange={e => setNewPerson({...newPerson, location: e.target.value})}
                    placeholder="e.g., Des Moines, Iowa"
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Birth Date</label>
                  <input
                    type="text"
                    value={newPerson.birthDate}
                    onChange={e => setNewPerson({...newPerson, birthDate: e.target.value})}
                    placeholder="e.g., August 7, 2000"
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Birth Place</label>
                  <input
                    type="text"
                    value={newPerson.birthPlace}
                    onChange={e => setNewPerson({...newPerson, birthPlace: e.target.value})}
                    placeholder="e.g., Des Moines, Iowa"
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Death Date</label>
                  <input
                    type="text"
                    value={newPerson.deathDate}
                    onChange={e => setNewPerson({...newPerson, deathDate: e.target.value})}
                    placeholder="Leave blank if living"
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Death Place</label>
                  <input
                    type="text"
                    value={newPerson.deathPlace}
                    onChange={e => setNewPerson({...newPerson, deathPlace: e.target.value})}
                    placeholder="Leave blank if living"
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Marriage Date</label>
                  <input
                    type="text"
                    value={newPerson.marriageDate}
                    onChange={e => setNewPerson({...newPerson, marriageDate: e.target.value})}
                    placeholder="e.g., September 1909"
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Marriage Place</label>
                  <input
                    type="text"
                    value={newPerson.marriagePlace}
                    onChange={e => setNewPerson({...newPerson, marriagePlace: e.target.value})}
                    placeholder="e.g., Frassinoro, Italy"
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'block', color: '#78350f', fontWeight: '500', marginBottom: '4px' }}>Story</label>
                <textarea
                  value={newPerson.story}
                  onChange={e => setNewPerson({...newPerson, story: e.target.value})}
                  placeholder="Tell their story..."
                  rows={4}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d97706', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button
                  onClick={() => setShowAddPerson(false)}
                  style={{ padding: '10px 20px', borderRadius: '6px', border: '1px solid #d97706', backgroundColor: 'transparent', color: '#78350f', cursor: 'pointer', fontSize: '14px' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddPerson}
                  style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#78350f', color: '#fef3c7', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
                >
                  Add Person
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}